/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Hero Supporting Section - What AI Twin Videos Can FalcoCut Make (3 tool cards)
 */
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface ToolCard {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const tools: ToolCard[] = [
  {
    id: 'image-to-video',
    title: 'Image to Video',
    description:
      'Upload a single headshot to animate a dynamic spokesperson. Our engine handles the facial mapping and natural lip-syncing for instant, high-quality video.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/image-to-video.webp',
  },
  {
    id: 'text-to-video',
    title: 'Text to Video',
    description:
      'Type your copy and watch your digital twin deliver it with human-like cadence. Ideal for scaling personalized outreach and rapid-fire content without a camera.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/text-to-video.webp',
  },
  {
    id: 'voice-to-video',
    title: 'Voice to Video',
    description:
      "Record or upload a clip to drive your AI double's performance. Every syllable and expression syncs to your unique vocal profile for total brand authenticity.",
    imageUrl: 'https://assets.falcocut.ai/tools/tiktok-video-generator/ai-avatars.webp',
  },
];

const HeroSupportingSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F9FAFB] py-[60px] pad:py-[70px] sm:py-[80px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pad:mb-10 sm:mb-12">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            What AI Twin Videos Can FalcoCut Make
          </h2>
          <p className="text-sm pad:text-base text-gray-600 max-w-[900px] mx-auto leading-relaxed">
            Make expressive AI twin videos faster by starting with a photo, a script, or
            your own voice. Generate engaging talking avatars for UGC-style reviews,
            product showcases, quick updates and trending content without cameras,
            studios, or complex tools.
          </p>
        </div>

        {/* Tool Cards - 3 columns */}
        <div className="flex flex-col sm:flex-row gap-6 pad:gap-5 sm:gap-6 justify-center items-center sm:items-stretch mb-8 pad:mb-10 sm:mb-12">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className="w-full max-w-[340px] sm:max-w-none sm:flex-1 flex flex-col"
            >
              {/* Image */}
              <div className="relative w-full aspect-square rounded-2xl pad:rounded-xl overflow-hidden bg-gray-200 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg sm:hover:-translate-y-1 sm:hover:shadow-lg">
                <Image
                  src={tool.imageUrl}
                  alt={tool.title}
                  width={340}
                  height={340}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Text */}
              <div className="pt-4 pad:pt-4 sm:pt-5 text-center sm:text-left">
                <h3 className="text-base pad:text-lg sm:text-xl font-semibold text-gray-900 mb-1.5 pad:mb-2">
                  {tool.title}
                </h3>
                <p className="text-xs pad:text-sm sm:text-sm text-gray-500 leading-relaxed">
                  {tool.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center h-12 pad:h-[42px] px-8 pad:px-7 text-base pad:text-[15px] font-medium text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
            }}
          >
            Create Your AI Twin
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSupportingSection;
